import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/ProjectDashboard.css';
import { API_BASE_URL } from '../config';
import { useAuth } from '../context/AuthContext';
import { fetchWithAuth } from '../utils/api';
import EvaluationResults from './EvaluationResults';
import GitHubPanel from './GitHubPanel';

function ProjectDashboard() {
  const { user, session } = useAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [overallStats, setOverallStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [expandedProject, setExpandedProject] = useState(null);
  const [expandedGitHub, setExpandedGitHub] = useState(null);

  // Review modal state
  const [reviewModal, setReviewModal] = useState(null);
  const [reviewFeedback, setReviewFeedback] = useState('');
  const [reviewLoading, setReviewLoading] = useState(false);
  const [taskDetails, setTaskDetails] = useState(null);
  const [taskEvaluation, setTaskEvaluation] = useState(null);

  const fetchDashboard = useCallback(async () => {
    if (!user) return;

    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams();
      if (filterStatus !== 'all') {
        params.append('status', filterStatus);
      }

      const url = `${API_BASE_URL}/problems/dashboard${params.toString() ? '?' + params.toString() : ''}`;
      const response = await fetchWithAuth(url, {}, session);

      if (!response.ok) {
        throw new Error('Failed to fetch dashboard data');
      }

      const data = await response.json();
      setProjects(data.data || []);
      setOverallStats(data.overallStats || null);
    } catch (err) {
      console.error('Error fetching dashboard:', err);
      setError('Failed to load dashboard. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, [user, session, filterStatus]);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  const openReviewModal = async (task) => {
    setReviewModal(task);
    setReviewFeedback('');
    setTaskDetails(null);
    setTaskEvaluation(null);

    // Fetch task details and evaluation
    try {
      const [taskRes, evalRes] = await Promise.all([
        fetchWithAuth(`${API_BASE_URL}/tasks/${task.id}`, {}, session),
        fetch(`${API_BASE_URL}/tasks/${task.id}/evaluation`)
      ]);

      if (taskRes.ok) {
        const taskData = await taskRes.json();
        setTaskDetails(taskData.task);
      }

      if (evalRes.ok) {
        const evalData = await evalRes.json();
        setTaskEvaluation(evalData.evaluation);
      }
    } catch (err) {
      console.error('Error fetching task details:', err);
    }
  };

  const closeReviewModal = () => {
    setReviewModal(null);
    setReviewFeedback('');
    setTaskDetails(null);
    setTaskEvaluation(null);
  };

  const handleReviewTask = async (taskId, status) => {
    setReviewLoading(true);
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/tasks/${taskId}/review`, {
        method: 'POST',
        body: JSON.stringify({
          status,
          feedback: status === 'rejected' ? reviewFeedback : undefined
        })
      }, session);

      if (!response.ok) {
        throw new Error('Failed to review task');
      }

      closeReviewModal();
      fetchDashboard();
    } catch (err) {
      console.error('Error reviewing task:', err);
      alert('Failed to review task. Please try again.');
    } finally {
      setReviewLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending_payment':
        return '#f59e0b';
      case 'open':
        return '#3b82f6';
      case 'in_progress':
        return '#8b5cf6';
      case 'completed':
        return '#10b981';
      case 'cancelled':
        return '#ef4444';
      default:
        return '#6b7280';
    }
  };

  const getTaskStatusColor = (status) => {
    switch (status) {
      case 'available':
        return '#3b82f6';
      case 'claimed':
        return '#f59e0b';
      case 'in_progress':
        return '#8b5cf6';
      case 'submitted':
        return '#06b6d4';
      case 'approved':
        return '#10b981';
      case 'rejected':
        return '#ef4444';
      default:
        return '#6b7280';
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (!user) {
    return (
      <div className="project-dashboard">
        <div className="login-prompt">
          <h2>Please log in to view your project dashboard</h2>
          <p>You need to be authenticated to see your projects and their tasks.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="project-dashboard">
      <div className="dashboard-header">
        <div className="header-content">
          <h1>Project Dashboard</h1>
          <p className="dashboard-subtitle">Manage your projects, track tasks, and monitor budgets</p>
        </div>
        <button className="create-project-btn" onClick={() => navigate('/agent-dashboard')}>
          + Create Project
        </button>
      </div>

      {/* Overall Stats Cards */}
      {overallStats && (
        <div className="overall-stats">
          <div className="stat-card">
            <div className="stat-value">{overallStats.totalProjects}</div>
            <div className="stat-label">Total Projects</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{formatCurrency(overallStats.totalBudget)}</div>
            <div className="stat-label">Total Budget</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{formatCurrency(overallStats.totalPaidOut)}</div>
            <div className="stat-label">Paid Out</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{formatCurrency(overallStats.totalRemaining)}</div>
            <div className="stat-label">Remaining</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{overallStats.tasksCompleted}/{overallStats.totalTasks}</div>
            <div className="stat-label">Tasks Completed</div>
          </div>
          <div className="stat-card highlight">
            <div className="stat-value">{overallStats.tasksPendingReview}</div>
            <div className="stat-label">Pending Review</div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="filters">
        <div className="filter-group">
          <label>Project Status:</label>
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
            <option value="all">All Projects</option>
            <option value="pending_payment">Pending Payment</option>
            <option value="open">Open</option>
            <option value="in_progress">In Progress</option>
            <option value="completed">Completed</option>
          </select>
        </div>
      </div>

      {loading && (
        <div className="loading-message">Loading your projects...</div>
      )}

      {error && (
        <div className="error-message">{error}</div>
      )}

      {!loading && !error && projects.length === 0 && (
        <div className="empty-state">
          <h3>No projects yet</h3>
          <p>Create your first project to get started!</p>
          <button className="create-project-btn" onClick={() => navigate('/agent-dashboard')}>
            Create Your First Project
          </button>
        </div>
      )}

      {/* Project List */}
      <div className="projects-list">
        {projects.map((project) => (
          <div key={project.id} className="project-card">
            <div className="project-header">
              <div className="project-info">
                <h3 className="project-title">{project.title}</h3>
                <span
                  className="project-status"
                  style={{ backgroundColor: getStatusColor(project.status) }}
                >
                  {project.status.replace('_', ' ')}
                </span>
              </div>
              <div className="project-meta">
                <span className="project-date">Created {formatDate(project.created_at)}</span>
                <span className="project-category">{project.category}</span>
              </div>
            </div>

            {/* Progress Bar */}
            <div className="progress-section">
              <div className="progress-header">
                <span>Progress</span>
                <span className="progress-percentage">{project.progressPercentage}%</span>
              </div>
              <div className="progress-bar">
                <div
                  className="progress-fill"
                  style={{ width: `${project.progressPercentage}%` }}
                />
              </div>
            </div>

            {/* Budget Stats */}
            <div className="budget-section">
              <h4>Budget Overview</h4>
              <div className="budget-grid">
                <div className="budget-item">
                  <span className="budget-label">Total Budget</span>
                  <span className="budget-value">{formatCurrency(project.budgetStats.total)}</span>
                </div>
                <div className="budget-item">
                  <span className="budget-label">Paid Out</span>
                  <span className="budget-value paid">{formatCurrency(project.budgetStats.paid_out)}</span>
                </div>
                <div className="budget-item">
                  <span className="budget-label">Pending Review</span>
                  <span className="budget-value pending">{formatCurrency(project.budgetStats.pending_review)}</span>
                </div>
                <div className="budget-item">
                  <span className="budget-label">In Progress</span>
                  <span className="budget-value in-progress">{formatCurrency(project.budgetStats.in_progress)}</span>
                </div>
                <div className="budget-item">
                  <span className="budget-label">Remaining</span>
                  <span className="budget-value remaining">{formatCurrency(project.budgetStats.remaining)}</span>
                </div>
              </div>
            </div>

            {/* Task Stats */}
            <div className="task-stats-section">
              <h4>Task Summary</h4>
              <div className="task-stats-grid">
                <div className="task-stat" style={{ '--stat-color': '#3b82f6' }}>
                  <span className="task-stat-value">{project.taskStats.available}</span>
                  <span className="task-stat-label">Available</span>
                </div>
                <div className="task-stat" style={{ '--stat-color': '#f59e0b' }}>
                  <span className="task-stat-value">{project.taskStats.claimed}</span>
                  <span className="task-stat-label">Claimed</span>
                </div>
                <div className="task-stat" style={{ '--stat-color': '#8b5cf6' }}>
                  <span className="task-stat-value">{project.taskStats.in_progress}</span>
                  <span className="task-stat-label">In Progress</span>
                </div>
                <div className="task-stat" style={{ '--stat-color': '#06b6d4' }}>
                  <span className="task-stat-value">{project.taskStats.submitted}</span>
                  <span className="task-stat-label">Submitted</span>
                </div>
                <div className="task-stat" style={{ '--stat-color': '#10b981' }}>
                  <span className="task-stat-value">{project.taskStats.approved}</span>
                  <span className="task-stat-label">Approved</span>
                </div>
                <div className="task-stat" style={{ '--stat-color': '#ef4444' }}>
                  <span className="task-stat-value">{project.taskStats.rejected}</span>
                  <span className="task-stat-label">Rejected</span>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="project-actions">
              <button
                className="expand-tasks-btn"
                onClick={() => setExpandedProject(expandedProject === project.id ? null : project.id)}
              >
                {expandedProject === project.id ? 'Hide Tasks' : `View Tasks (${project.taskStats.total})`}
              </button>
              <button
                className={`github-btn ${expandedGitHub === project.id ? 'active' : ''}`}
                onClick={() => setExpandedGitHub(expandedGitHub === project.id ? null : project.id)}
              >
                <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
                  <path d="M12 0C5.37 0 0 5.37 0 12c0 5.31 3.435 9.795 8.205 11.385.6.105.825-.255.825-.57 0-.285-.015-1.23-.015-2.235-3.015.555-3.795-.735-4.035-1.41-.135-.345-.72-1.41-1.23-1.695-.42-.225-1.02-.78-.015-.795.945-.015 1.62.87 1.845 1.23 1.08 1.815 2.805 1.305 3.495.99.105-.78.42-1.305.765-1.605-2.67-.3-5.46-1.335-5.46-5.925 0-1.305.465-2.385 1.23-3.225-.12-.3-.54-1.53.12-3.18 0 0 1.005-.315 3.3 1.23.96-.27 1.98-.405 3-.405s2.04.135 3 .405c2.295-1.56 3.3-1.23 3.3-1.23.66 1.65.24 2.88.12 3.18.765.84 1.23 1.905 1.23 3.225 0 4.605-2.805 5.625-5.475 5.925.435.375.81 1.095.81 2.22 0 1.605-.015 2.895-.015 3.3 0 .315.225.69.825.57A12.02 12.02 0 0024 12c0-6.63-5.37-12-12-12z"/>
                </svg>
                {expandedGitHub === project.id ? 'Hide GitHub' : 'GitHub'}
              </button>
            </div>

            {/* GitHub Panel (Expanded) */}
            {expandedGitHub === project.id && (
              <div className="github-section">
                <GitHubPanel problemId={project.id} />
              </div>
            )}

            {/* Task List (Expanded) */}
            {expandedProject === project.id && (
              <div className="tasks-section">
                {project.tasks.length === 0 ? (
                  <div className="no-tasks">
                    {project.task_generation_status === 'pending' || project.task_generation_status === 'in_progress' ? (
                      <p>Tasks are being generated from your PRD...</p>
                    ) : (
                      <p>No tasks have been created for this project yet.</p>
                    )}
                  </div>
                ) : (
                  <div className="tasks-table">
                    <div className="table-header">
                      <div className="col-title">Task</div>
                      <div className="col-status">Status</div>
                      <div className="col-worker">Worker</div>
                      <div className="col-bounty">Bounty</div>
                      <div className="col-actions">Actions</div>
                    </div>
                    {project.tasks.map((task) => (
                      <div key={task.id} className="table-row">
                        <div className="col-title">
                          <span className="task-title-text">{task.title}</span>
                          <div className="task-badges">
                            <span
                              className="priority-badge"
                              style={{ backgroundColor: task.priority === 'high' ? '#ef4444' : task.priority === 'medium' ? '#f59e0b' : '#10b981' }}
                            >
                              {task.priority}
                            </span>
                            <span className="complexity-badge">{task.complexity}</span>
                          </div>
                        </div>
                        <div className="col-status">
                          <span
                            className="status-badge"
                            style={{ backgroundColor: getTaskStatusColor(task.status) }}
                          >
                            {task.status.replace('_', ' ')}
                          </span>
                          {task.evaluation_status === 'failed' && (
                            <span className="error-indicator" title={task.evaluation_error || 'Evaluation failed'}>
                              ⚠️ Eval Error
                            </span>
                          )}
                          {task.rating_update_status === 'failed' && (
                            <span className="error-indicator" title={task.rating_update_error || 'Rating update failed'}>
                              ⚠️ Rating Error
                            </span>
                          )}
                        </div>
                        <div className="col-worker">
                          {task.assigned_worker ? (
                            <span className="worker-name">{task.assigned_worker.username || task.assigned_worker.email}</span>
                          ) : (
                            <span className="no-worker">Unassigned</span>
                          )}
                        </div>
                        <div className="col-bounty">
                          {formatCurrency(task.bounty_portion)}
                        </div>
                        <div className="col-actions">
                          {task.status === 'submitted' && (
                            <button
                              className="review-btn"
                              onClick={() => openReviewModal(task)}
                            >
                              Review
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Review Modal */}
      {reviewModal && (
        <div className="review-modal-overlay" onClick={closeReviewModal}>
          <div className="review-modal" onClick={(e) => e.stopPropagation()}>
            <div className="review-modal-header">
              <h2>Review Submission</h2>
              <button className="close-modal-btn" onClick={closeReviewModal}>&times;</button>
            </div>

            <div className="review-modal-content">
              {/* Task Info */}
              <div className="review-task-info">
                <h3>{reviewModal.title}</h3>
                <div className="review-task-meta">
                  <span className="priority-badge" style={{
                    backgroundColor: reviewModal.priority === 'high' ? '#ef4444' : reviewModal.priority === 'medium' ? '#f59e0b' : '#10b981'
                  }}>
                    {reviewModal.priority}
                  </span>
                  <span className="complexity-badge">{reviewModal.complexity}</span>
                  <span className="bounty-badge">{formatCurrency(reviewModal.bounty_portion)}</span>
                </div>
                {reviewModal.assigned_worker && (
                  <p className="worker-info">
                    Submitted by: <strong>{reviewModal.assigned_worker.username || reviewModal.assigned_worker.email}</strong>
                  </p>
                )}
              </div>

              {/* Solution Section */}
              <div className="review-solution-section">
                <h4>Submitted Solution</h4>
                {taskDetails?.solution_text ? (
                  <div className="solution-text">
                    <pre>{taskDetails.solution_text}</pre>
                  </div>
                ) : (
                  <p className="loading-text">Loading solution...</p>
                )}

                {taskDetails?.github_pr_url && (
                  <div className="github-pr-link">
                    <strong>GitHub PR:</strong>{' '}
                    <a href={taskDetails.github_pr_url} target="_blank" rel="noopener noreferrer">
                      {taskDetails.github_pr_url}
                    </a>
                  </div>
                )}
              </div>

              {/* Evaluation Results */}
              <div className="review-evaluation-section">
                <h4>AI Evaluation</h4>
                <EvaluationResults evaluation={taskEvaluation} />
              </div>

              {/* Feedback Input */}
              <div className="review-feedback-section">
                <h4>Feedback (required for rejection)</h4>
                <textarea
                  value={reviewFeedback}
                  onChange={(e) => setReviewFeedback(e.target.value)}
                  placeholder="Provide feedback for the worker..."
                  rows={4}
                />
              </div>
            </div>

            <div className="review-modal-actions">
              <button
                className="approve-btn"
                onClick={() => handleReviewTask(reviewModal.id, 'approved')}
                disabled={reviewLoading}
              >
                {reviewLoading ? 'Processing...' : 'Approve'}
              </button>
              <button
                className="reject-btn"
                onClick={() => handleReviewTask(reviewModal.id, 'rejected')}
                disabled={reviewLoading || !reviewFeedback.trim()}
              >
                {reviewLoading ? 'Processing...' : 'Reject'}
              </button>
              <button
                className="cancel-btn"
                onClick={closeReviewModal}
                disabled={reviewLoading}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ProjectDashboard;
